import React from 'react';
import classNames from 'classnames';

import Icons from 'assets/icons';

export type NotificationVariant = 'info' | 'success' | 'error' | 'warning';

interface NotificationMessageProps {
  heading: string;
  message?: string;
  variant: NotificationVariant;
  removeNotification?: () => void;
  customClass?: string;
  children?: React.ReactNode;
}

const NotificationMessage = ({
  heading,
  message = '',
  variant,
  removeNotification,
  customClass,
  children,
}: NotificationMessageProps): React.JSX.Element => {
  const defaultIconProps = {
    customClass: 'mr-2 w-6 h-6',
  };

  const notificationParams = {
    info: {
      textColor: 'text-navy-800',
      borderColor: 'border-navy-800',
      icon: <Icons.Info aria-label="info-icon" {...defaultIconProps} />,
    },
    success: {
      textColor: 'text-lime-700',
      borderColor: 'border-lime-700',
      icon: <Icons.Success aria-label="success-icon" {...defaultIconProps} />,
    },
    error: {
      textColor: 'text-critical-500',
      borderColor: 'border-critical-500',
      icon: <Icons.Warning aria-label="error-icon" {...defaultIconProps} />,
    },
    warning: {
      textColor: 'text-amber-500',
      borderColor: 'border-amber-500',
      icon: <Icons.Warning aria-label="warning-icon" {...defaultIconProps} />,
    },
  };

  const { borderColor, textColor } = notificationParams[variant];

  return (
    <div
      className={`border rounded-md w-full mt-5 p-4 justify-between flex ${borderColor} ${customClass}`}
      data-testid="notification-message"
    >
      <div className="flex items-start">
        <span className={`${textColor}`}>
          {notificationParams[variant]?.icon}
        </span>
        <div
          className="flex flex-col"
          data-testid="notification-message-content"
        >
          <span
            className={`text-base font-semibold ${textColor}`}
            data-testid="notification-message-heading"
          >
            {heading}
          </span>
          {message && (
            <span
              className="text-sm mt-2"
              data-testid="notification-message-message"
            >
              {message}
            </span>
          )}
          {children}
        </div>
      </div>
      {removeNotification && (
        <Icons.Close
          className={classNames('cursor-pointer', textColor, {
            'mt-1': !message,
          })}
          onClick={removeNotification}
          data-testid="notification-message-close"
        />
      )}
    </div>
  );
};

export default NotificationMessage;
